import {computed, ref} from 'vue'
import type {Connection, Edge, Node} from '@vue-flow/core'
import type {NodeType, WorkflowNodeData} from '@/workflow/types'

export function useWorkflowEditor() {
    const nodes = ref<Node[]>([])
    const edges = ref<Edge[]>([])
    const selectedNode = ref<Node | null>(null)
    const validationError = ref<string>('')
    const isDraggingFromPalette = ref(false)

    // Node types for palette
    const nodeTypes: NodeType[] = [
        {
            type: 'spark',
            label: 'Spark (Trigger)',
            icon: 'i-ph-lightning-fill',
            color: 'bg-green-500',
            description: 'Workflow trigger'
        },
        {
            type: 'action',
            label: 'Action',
            icon: 'i-ph-gear-fill',
            color: 'bg-blue-500',
            description: 'Execute action'
        },
        {
            type: 'branch',
            label: 'Branch',
            icon: 'i-ph-tree-structure-fill',
            color: 'bg-yellow-500',
            description: 'Conditional branch'
        },
        {
            type: 'join',
            label: 'Join',
            icon: 'i-ph-intersect-fill',
            color: 'bg-orange-500',
            description: 'Join multiple paths'
        }
    ]

    // Validation
    const isValid = computed(() => {
        return validateWorkflowGraph()
    })

    function validateWorkflowGraph(): boolean {
        validationError.value = ''

        // Check for cycles using DFS
        if (hasCycles()) {
            validationError.value = 'Workflow contains cycles'
            return false
        }

        // Check for orphaned nodes
        const sparkNodes = nodes.value.filter(n => n.type === 'spark')
        if (sparkNodes.length === 0) {
            validationError.value = 'Workflow must have at least one Spark node'
            return false
        }

        return true
    }

    function hasCycles(): boolean {
        // Simple cycle detection using DFS
        const visited = new Set<string>()
        const recursionStack = new Set<string>()

        function dfs(nodeId: string): boolean {
            if (recursionStack.has(nodeId)) return true
            if (visited.has(nodeId)) return false

            visited.add(nodeId)
            recursionStack.add(nodeId)

            const outgoingEdges = edges.value.filter(e => e.source === nodeId)
            for (const edge of outgoingEdges) {
                if (dfs(edge.target)) return true
            }

            recursionStack.delete(nodeId)
            return false
        }

        for (const node of nodes.value) {
            if (!visited.has(node.id)) {
                if (dfs(node.id)) return true
            }
        }
        return false
    }

    // Event handlers
    function onNodesChange(changes: any[]) {
        // Handle node position updates and other changes
        changes.forEach(change => {
            if (change.type === 'position' && change.position && change.dragging !== false) {
                const node = nodes.value.find(n => n.id === change.id)
                if (node) {
                    // Only update position if it's not during dragging end
                    node.position = {
                        x: Math.round(change.position.x),
                        y: Math.round(change.position.y)
                    }
                }
            }
            if (change.type === 'select') {
                const node = nodes.value.find(n => n.id === change.id)
                selectedNode.value = change.selected ? node || null : null
            }
            if (change.type === 'add') {
                // Handle node addition
                const newNode = change.item
                if (!nodes.value.find(n => n.id === newNode.id)) {
                    nodes.value.push(newNode)
                }
            }
            if (change.type === 'remove') {
                // Handle node removal
                const index = nodes.value.findIndex(n => n.id === change.id)
                if (index > -1) {
                    nodes.value.splice(index, 1)
                }
            }
        })
    }

    function onEdgesChange(changes: any[]) {
        // Handle edge changes (add, remove, etc.)
        changes.forEach(change => {
            if (change.type === 'remove') {
                const index = edges.value.findIndex(e => e.id === change.id)
                if (index > -1) {
                    edges.value.splice(index, 1)
                }
            }
        })
    }

    function onConnect(connection: Connection) {
        if (!connection.source || !connection.target) return

        // Create a more specific edge ID that includes handle information
        const sourceHandle = connection.sourceHandle ? `-${connection.sourceHandle}` : ''
        const targetHandle = connection.targetHandle ? `-${connection.targetHandle}` : ''
        const edgeId = `${connection.source}${sourceHandle}_to_${connection.target}${targetHandle}`

        // Check if edge already exists
        const existingEdge = edges.value.find(e =>
            e.source === connection.source &&
            e.target === connection.target &&
            e.sourceHandle === connection.sourceHandle &&
            e.targetHandle === connection.targetHandle
        )
        if (existingEdge) {
            console.warn('Connection already exists')
            return
        }

        // Create new edge
        const newEdge: Edge = {
            id: edgeId,
            source: connection.source,
            target: connection.target,
            sourceHandle: connection.sourceHandle || undefined,
            targetHandle: connection.targetHandle || undefined,
            type: 'smoothstep',
            animated: true,
            style: {stroke: '#374151', strokeWidth: 2}
        }

        // Temporarily add the edge to check for cycles
        const tempEdges = [...edges.value, newEdge]
        const originalEdges = edges.value
        edges.value = tempEdges

        if (hasCycles()) {
            // Revert if cycle detected
            edges.value = originalEdges
            console.warn('Connection would create a cycle and was rejected')
            return
        }

        // Connection is valid, keep the new edge
        console.log('Connected:', connection.source, 'to', connection.target, 'via handles:', connection.sourceHandle, '->', connection.targetHandle)
    }

    function onNodeDragStop(event: any) {
        // Finalize node position when drag stops
        const {node} = event
        const existingNode = nodes.value.find(n => n.id === node.id)
        if (existingNode) {
            existingNode.position = {
                x: Math.round(node.position.x),
                y: Math.round(node.position.y)
            }
        }
    }

    function onCreateNode(nodeType: NodeType, position?: { x: number, y: number }) {
        const nodeId = `${nodeType.type}-${Date.now()}`
        const finalPosition = position || {
            x: Math.random() * 400 + 100,
            y: Math.random() * 400 + 100
        }

        const newNode: Node<WorkflowNodeData> = {
            id: nodeId,
            type: nodeType.type,
            position: {
                x: Math.round(finalPosition.x),
                y: Math.round(finalPosition.y)
            },
            data: {
                id: nodeId,
                label: `${nodeType.label} ${nodes.value.length + 1}`,
                type: nodeType.type,
                properties: {}
            }
        }

        nodes.value.push(newNode)

        // Select the newly created node
        setTimeout(() => {
            selectedNode.value = newNode
        }, 50)
    }

    function onDrop(event: DragEvent) {
        event.preventDefault()
        isDraggingFromPalette.value = false

        const nodeTypeData = event.dataTransfer?.getData('application/vueflow-node')
        if (!nodeTypeData) return

        try {
            const nodeType: NodeType = JSON.parse(nodeTypeData)

            // Get the Vue Flow canvas element - look for the actual flow container
            const canvas = (event.currentTarget as HTMLElement).querySelector('.vue-flow__viewport') as HTMLElement
            const canvasContainer = event.currentTarget as HTMLElement

            let position: { x: number, y: number }

            if (canvas) {
                // Get the viewport transform to account for pan/zoom
                const transform = window.getComputedStyle(canvas).transform
                let translateX = 0, translateY = 0, scale = 1

                if (transform && transform !== 'none') {
                    const matrix = new DOMMatrix(transform)
                    translateX = matrix.e
                    translateY = matrix.f
                    scale = matrix.a
                }

                // Calculate position relative to the container and account for viewport transform
                const containerRect = canvasContainer.getBoundingClientRect()
                const rawX = (event.clientX - containerRect.left - translateX) / scale
                const rawY = (event.clientY - containerRect.top - translateY) / scale

                // Offset by half the typical node size to center the node on the mouse cursor
                const nodeOffsetX = 80 // Approximate half width of a node
                const nodeOffsetY = 20 // Approximate half height of a node

                position = {
                    x: Math.round(rawX - nodeOffsetX),
                    y: Math.round(rawY - nodeOffsetY)
                }
            } else {
                // Fallback to simple container-relative calculation
                const containerRect = canvasContainer.getBoundingClientRect()
                const nodeOffsetX = 80 // Approximate half width of a node
                const nodeOffsetY = 20 // Approximate half height of a node

                position = {
                    x: Math.round(event.clientX - containerRect.left - nodeOffsetX),
                    y: Math.round(event.clientY - containerRect.top - nodeOffsetY)
                }
            }

            onCreateNode(nodeType, position)
        } catch (error) {
            console.error('Failed to parse dropped node data:', error)
        }
    }

    function onDragOver(event: DragEvent) {
        event.preventDefault()
        event.dataTransfer!.dropEffect = 'copy'

        // Check if we're dragging from palette
        const nodeTypeData = event.dataTransfer?.getData('application/vueflow-node')
        if (nodeTypeData && !isDraggingFromPalette.value) {
            isDraggingFromPalette.value = true
        }
    }

    function onDragLeave(event: DragEvent) {
        // Only reset if we're leaving the canvas entirely
        const canvas = event.currentTarget as HTMLElement
        const rect = canvas.getBoundingClientRect()

        if (event.clientX < rect.left || event.clientX > rect.right ||
            event.clientY < rect.top || event.clientY > rect.bottom) {
            isDraggingFromPalette.value = false
        }
    }

    function onNodeClick(event: any) {
        const node = nodes.value.find(n => n.id === event.node.id)
        selectedNode.value = node || null
    }

    function onNodeUpdate(nodeId: string, updates: Partial<WorkflowNodeData>) {
        const node = nodes.value.find(n => n.id === nodeId)
        if (node && node.data) {
            Object.assign(node.data, updates)
        }
    }

    function createDemoWorkflow() {
        // Clear existing
        nodes.value = []
        edges.value = []

        // Create demo nodes
        const sparkNode: Node<WorkflowNodeData> = {
            id: 'spark-1',
            type: 'spark',
            position: {x: 100, y: 100},
            data: {
                id: 'spark-1',
                label: 'Invoice Received',
                type: 'spark',
                properties: {sparkIdentifier: 'invoice-received'}
            }
        }

        const actionNode1: Node<WorkflowNodeData> = {
            id: 'action-1',
            type: 'action',
            position: {x: 300, y: 100},
            data: {
                id: 'action-1',
                label: 'Validate Invoice',
                type: 'action',
                properties: {}
            }
        }

        const branchNode: Node<WorkflowNodeData> = {
            id: 'branch-1',
            type: 'branch',
            position: {x: 500, y: 100},
            data: {
                id: 'branch-1',
                label: 'Valid Invoice?',
                type: 'branch',
                properties: {}
            }
        }

        const actionNode2: Node<WorkflowNodeData> = {
            id: 'action-2',
            type: 'action',
            position: {x: 700, y: 50},
            data: {
                id: 'action-2',
                label: 'Process Payment',
                type: 'action',
                properties: {}
            }
        }

        const actionNode3: Node<WorkflowNodeData> = {
            id: 'action-3',
            type: 'action',
            position: {x: 700, y: 150},
            data: {
                id: 'action-3',
                label: 'Reject Invoice',
                type: 'action',
                properties: {}
            }
        }

        nodes.value = [sparkNode, actionNode1, branchNode, actionNode2, actionNode3]

        // Create demo edges
        edges.value = [
            {id: 'spark-1->action-1', source: 'spark-1', target: 'action-1', animated: true},
            {id: 'action-1->branch-1', source: 'action-1', target: 'branch-1', animated: true},
            {id: 'branch-1->action-2', source: 'branch-1', target: 'action-2', animated: true},
            {id: 'branch-1->action-3', source: 'branch-1', target: 'action-3', animated: true}
        ]
    }

    function validateWorkflow() {
        // Trigger validation
        isValid.value
    }

    function autoLayout() {
        // Simple auto-layout using a basic algorithm
        const layerSpacing = 200
        const nodeSpacing = 150

        // Group nodes by dependencies (simple layering)
        const layers: Node[][] = []

        function getLayer(node: Node): number {
            const incomingEdges = edges.value.filter(e => e.target === node.id)
            if (incomingEdges.length === 0) return 0

            let maxSourceLayer = -1
            for (const edge of incomingEdges) {
                const sourceNode = nodes.value.find(n => n.id === edge.source)
                if (sourceNode) {
                    maxSourceLayer = Math.max(maxSourceLayer, getLayer(sourceNode))
                }
            }
            return maxSourceLayer + 1
        }

        // Assign layers
        for (const node of nodes.value) {
            const layer = getLayer(node)
            while (layers.length <= layer) {
                layers.push([])
            }
            layers[layer].push(node)
        }

        // Position nodes
        layers.forEach((layer, layerIndex) => {
            layer.forEach((node, nodeIndex) => {
                node.position = {
                    x: layerIndex * layerSpacing + 100,
                    y: nodeIndex * nodeSpacing + 100
                }
            })
        })
    }

    return {
        nodes,
        edges,
        selectedNode,
        isValid,
        validationError,
        nodeTypes,
        isDraggingFromPalette,
        onNodesChange,
        onEdgesChange,
        onConnect,
        onNodeDragStop,
        onCreateNode,
        onNodeUpdate,
        onNodeClick,
        onDrop,
        onDragOver,
        onDragLeave,
        createDemoWorkflow,
        validateWorkflow,
        autoLayout
    }
}
